/**
 * Module dependencies.
 */

import { Button, Image, Type } from 'src/components/core';
import { ReactElement } from 'react';
import { color, media, units } from '@untile/react-components';
import { theme } from 'styled-tools';
import { useLocalizedRoute } from 'src/core/utils/routes';
import Container from 'src/components/core/layout/container';
import styled from 'styled-components';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = {
  className?: string;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.section`
  padding-top: ${units(6)};
  position: relative;
  width: 100vw;

  ${media.min('md')`
    padding-top: ${units(31)};
  `}
`;

/**
 * `StyledContainer` styled component.
 */

const StyledContainer = styled(Container)`
  padding: 0;
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-template-areas:
    '.          titleWrapper titleWrapper .'
    '.          .            image        image'
    '.          .            image        image'
    'background background   background   background';
  grid-template-columns: ${theme('grid.gutterMobile')}px 11.7vw 1fr ${theme(
      'grid.gutterMobile'
    )}px;
  grid-template-rows: max-content ${units(10.5)} max-content ${units(6)};

  ${media.min('md')`
    grid-template-areas:
      '. titleWrapper . . .          image'
      '. titleWrapper . . background background'
      '. .            . . background background';
    grid-template-columns: 1fr 4fr 1fr ${theme('grid.columnsGutter')}px 1fr 5fr;
    grid-template-rows: ${units(20)} max-content ${units(15)};
  `}
`;

/**
 * `Background` styled component.
 */

const Background = styled.div`
  background-color: ${color('backgrounds.green02')};
  grid-area: background;
  z-index: -1;

  ${media.max('md')`
    grid-row: 3 / -1;
  `}
`;

/**
 * `TitleWrapper` styled component.
 */

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: titleWrapper;

  ${media.max('md')`
    margin-bottom: ${units(6)};
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H2)`
  margin-bottom: ${units(2)};
`;

/**
 * `Description` styled component.
 */

const Description = styled(Type.Paragraph)`
  margin-bottom: ${units(5)};

  ${media.min('md')`
    margin-bottom: ${units(3)};
  `}
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  grid-area: image;
  overflow: hidden;
  padding-bottom: 100%;
  position: relative;

  ${media.min('md')`
    grid-row: 1 / 3;
  `}
`;

/**
 * `SustainabilitySection` component.
 */

const SustainabilitySection = (props: Props): ReactElement => {
  const { className } = props;
  const { translate } = useTranslate();
  const routeResolver = useLocalizedRoute();
  const animProps = {
    'data-scroll': 'fade-in-up',
    'data-scroll-offset': '10%'
  };

  return (
    <Wrapper className={className}>
      <StyledContainer>
        <Grid>
          <TitleWrapper>
            <Title {...animProps}>
              {translate('about-us:sections.sustainability.title')}
            </Title>

            <Description {...animProps}>
              {translate('about-us:sections.sustainability.description')}
            </Description>

            <div {...animProps}>
              <Button
                colorTheme={'secondary'}
                href={routeResolver('sustainability')}
              >
                {translate('common:actions.exploreOurBalance')}
              </Button>
            </div>
          </TitleWrapper>

          <Background />

          <ImageWrapper
            data-scroll
            data-scroll-speed={1}
          >
            <Image
              alt={translate('about-us:sections.sustainability.title')}
              fill
              src={'/static/images/sustainability.jpg'}
              style={{ objectFit: 'cover' }}
            />
          </ImageWrapper>
        </Grid>
      </StyledContainer>
    </Wrapper>
  );
};

/**
 * Export `SustainabilitySection` component.
 */

export default SustainabilitySection;
